import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import Footer from "../../Home/Components/Footer/contact";
import { neighbours } from "../../../lib/projects";
import useReveal from "../../../lib/useReveal";
import "./case.scss";

gsap.registerPlugin(ScrollTrigger);

// Shared shell for every case study: opening, reading-progress bar, and the
// prev/next hand-off. Previously a deck ended at the footer with no way
// onward and no sense of how much was left.
const CaseStudy = ({
  slug,
  title,
  tagline,
  lead,
  year,
  roles = [],
  client,
  hero,
  links = [],
  children,
}) => {
  const barRef = useRef(null);
  const heroRef = useReveal({ threshold: 0.05 });
  const { prev, next } = neighbours(slug);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Wayfinding: a long case study needs to show how much is left.
    const st = ScrollTrigger.create({
      trigger: document.documentElement,
      start: "top top",
      end: "bottom bottom",
      onUpdate: (self) => gsap.set(bar, { scaleX: self.progress }),
    });
    return () => st.kill();
  }, []);

  return (
    <>
      <div className="cs-progress" aria-hidden="true">
        <span ref={barRef} />
      </div>

      <article className="cs">
        <header className="cs-hero" ref={heroRef}>
          <div className="cs-hero__copy">
            <Link to="/#work" className="cs-back">
              <ArrowLeft size={14} weight="bold" />
              All work
            </Link>

            <h1 className="cs-title" data-reveal>
              {title}
            </h1>
            {tagline && (
              <p className="cs-tagline" data-reveal style={{ "--d": 1 }}>
                {tagline}
              </p>
            )}
            {lead && (
              <div className="cs-hero__lead" data-reveal style={{ "--d": 2 }}>
                {lead}
              </div>
            )}

            <dl className="cs-meta" data-reveal style={{ "--d": 3 }}>
              {year && (
                <div>
                  <dt>Year</dt>
                  <dd>{year}</dd>
                </div>
              )}
              {client && (
                <div>
                  <dt>Client</dt>
                  <dd>
                    {client.href ? (
                      <a href={client.href} target="_blank" rel="noreferrer">
                        {client.name}
                      </a>
                    ) : (
                      client.name
                    )}
                  </dd>
                </div>
              )}
              {roles.length > 0 && (
                <div className="cs-meta__roles">
                  <dt>Role</dt>
                  <dd>
                    <ul>
                      {roles.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </dd>
                </div>
              )}
            </dl>

            {links.length > 0 && (
              <div className="cs-links" data-reveal style={{ "--d": 4 }}>
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className="cs-link"
                  >
                    {l.logo ? (
                      <img src={l.logo} alt={l.label} />
                    ) : (
                      <span>{l.label}</span>
                    )}
                    <ArrowUpRight size={16} weight="bold" aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {hero && (
            <div
              className={`cs-hero__media${hero.blend ? " cs-hero__media--blend" : ""}`}
              data-reveal
              style={{ "--d": 1 }}
            >
              <img
                src={hero.src}
                alt={hero.alt}
                loading="eager"
                decoding="async"
              />
            </div>
          )}
        </header>

        {children}

        <nav className="cs-next" aria-label="More work">
          {prev && (
            <Link to={prev.slug} className="cs-next__link cs-next__link--prev">
              <span className="u-mono">Previous</span>
              <strong>
                <ArrowLeft size={18} weight="bold" aria-hidden="true" />
                {prev.title}
              </strong>
            </Link>
          )}
          {next && (
            <Link to={next.slug} className="cs-next__link cs-next__link--next">
              <span className="u-mono">Next project</span>
              <strong>
                {next.title}
                <ArrowRight size={18} weight="bold" aria-hidden="true" />
              </strong>
            </Link>
          )}
        </nav>
      </article>

      <Footer />
    </>
  );
};

export default CaseStudy;
